// #region Imports
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import SnippetFolderIcon from "@mui/icons-material/Folder";
import { keyframes } from "@mui/material";
import Alert from "@mui/material/Alert";
import type { AlertColor } from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  ConfirmResponse,
  useConfirmDialog,
} from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { MRTNoMemo } from "_shared/_views/tables/table";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  type MRT_Row,
  type MRT_TableInstance,
  type MRT_TableOptions,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_SortingState as SortingState,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton
} from "material-react-table";
import { MouseEvent, useCallback, useMemo, useState } from "react";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency } from "work/_shared/masked-fields";
import {
  isFeeScheduleTemplateLoadIssue
} from "work/fee-schedule-templates/_store/fee-schedule-templates-redux-slice";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import { ProposalIssue } from "work/proposals/issues/proposal-issues";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import FeeScheduleCategory from "../../../../_shared/fee-schedules/categories/fee-schedule-category";
// #endregion

// #region Styled Components
const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const TableContainer = styled('div')(() => ({
  height: "100%",
}));
const ActionsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(1),
  justifyContent: "flex-end",
  marginBottom: theme.spacing(2),
  marginLeft: theme.spacing(1)
}));
const RowTitle = styled(Typography)(() => ({
  fontSize: "1em",
  fontWeight: "500",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const RowActionsContainer = styled("div")(() => ({
  whiteSpace: "nowrap",
}));
// #endregion

const columnHelper = createMRTColumnHelper<
  FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency
>();

// #region Props
type FeeScheduleTemplatesTableProps = {
  className?: string;
  selectedCategories:
  | Array<FeeScheduleCategory>
  | Array<FeeScheduleCategoryMaskedCurrency>;
  disableEditing?: boolean;
  disableCategorySelection?: boolean;
  hideFromTemplateButton?: boolean;
  hideAddButton?: boolean;
  hideExistingButton?: boolean;
  hideCommentButton?: boolean;
  hideActionsColumn?: boolean;
  issues?: ProposalIssue[];
  commentThreads?: CommentThread[];
  disableComments?: boolean;
  commentsToolTip: string;
  alertBannerMessage?: string;
  alertBannerSeverity?: AlertColor;
  activeReviewField?: ProposalField;
  onCommentsClicked?: (field: ProposalField, name?: string) => void;
  onCreateCategoryClicked?: () => void;
  onExistingCategoryClicked?: () => void;
  onExistingTemplateClicked?: () => void;
  onEditCategory?: (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => void;
  onCategoryRemoved?: (name: FeeScheduleCategoryName) => void;
};
// #endregion

export default function FeeScheduleTemplatesTable(
  props: Readonly<FeeScheduleTemplatesTableProps>,
) {
  const {
    className,
    selectedCategories,
    disableEditing,
    disableCategorySelection,
    hideFromTemplateButton,
    hideAddButton,
    hideExistingButton,
    hideCommentButton,
    hideActionsColumn,
    issues,
    commentThreads,
    disableComments,
    commentsToolTip,
    alertBannerMessage,
    alertBannerSeverity = "warning",
    activeReviewField,
    onCommentsClicked,
    onCreateCategoryClicked,
    onExistingCategoryClicked,
    onExistingTemplateClicked,
    onEditCategory,
    onCategoryRemoved,
  } = props;

  // #region State
  const [query, setQuery] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  // #region Hooks
  const session = useSession();
  const confirm = useConfirmDialog();

  // #region Functions
  const handleRemoveButtonClicked = useCallback(
    async (
      event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>,
      category:
        | FeeScheduleCategory
        | FeeScheduleCategoryMaskedCurrency
        | undefined
        | null,
    ) => {
      event.stopPropagation();

      if (!category) return;

      const response = await confirm({
        title: "Delete Fee Schedule Category?",
        message: `Delete the ${category.name} fee schedule category?`,
        okButtonText: "Delete",
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;

      onCategoryRemoved?.(category.name);
    },
    [confirm, onCategoryRemoved],
  );

  const beginEditCategory = useCallback(
    (
      category:
        | FeeScheduleCategory
        | FeeScheduleCategoryMaskedCurrency
        | undefined
        | null,
    ) => {
      if (!category || disableEditing) return;
      onEditCategory?.(category);
    },
    [disableEditing, onEditCategory],
  );
  // #endregion

  const FeeCell = useCallback(({ row, table }: {
    row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency },
    table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
  }) => {
    if (table.getState().isLoading || isFeeScheduleTemplateLoadIssue(row.original)) return;
    let feeValue;
    if (row.original instanceof FeeScheduleCategoryMaskedCurrency) {
      feeValue = "Masked";
    } else if (row.original.fee == null) {
      feeValue = (
        <Typography color="warning">{`Deferred to ${session.context?.viewingAsVendor ? "client" : "vendor"
          }`}</Typography>
      );
    } else {
      feeValue = new Intl.NumberFormat(navigator.language, {
        style: "currency",
        currency: row.original.fee?.rate?.currency.toString() ?? "USD",
      }).format(row.original.fee?.rate?.amount ?? 0);
    }
    return feeValue;
  }, [session.context?.viewingAsVendor]);

  // #region Columns
  const columns = useMemo(
    () => [
      columnHelper.display({
        header: "Name",
        id: "name",
        Cell: NameCell
      }),
      columnHelper.display({
        header: "Description",
        id: "description",
        enableSorting: false,
        Cell: DescriptionCell
      }),
      columnHelper.display({
        header: "Fixed?",
        id: "fixedFee",
        size: 50,
        enableSorting: false,
        Cell: FixedFeeCell
      }),
      columnHelper.display({
        header: "Fee",
        id: "fee",
        size: 50,
        Cell: FeeCell
      }),
      columnHelper.display({
        header: "Per",
        id: "unit",
        size: 50,
        enableSorting: false,
        Cell: UnitCell
      }),
      columnHelper.display({
        header: "Code",
        id: "billingCode",
        size: 50,
        enableSorting: false,
        Cell: BillingCodeCell
      }),
    ],
    [FeeCell]
  );
  // #endregion

  // #region Table Option Functions
  const getRowId = useCallback(
    (row: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => row?.id?.value,
    [],
  );

  const muiTableProps = useCallback(({ table }: {
    table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
  }) => ({
    sx: {
      borderSpacing: table
        .getRowModel()
        .rows.some((row) => row.id === activeReviewField?.id?.value)
        ? "3px"
        : 0,
    },
  }), [activeReviewField?.id?.value]);

  const muiTableBodyRowProps = useCallback(({ row }: {
    row: MRT_Row<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
  }) => {
    return disableCategorySelection
      ? {
        sx: {
          "& tr.MuiTableRow-root": {
            cursor: "default !important",
          },
          "& > td": {
            hover: !disableCategorySelection,
          }
        }
      }
      : {
        onClick: () => beginEditCategory(row.original),
        sx: {
          animation: row?.original?.id?.isEqualTo(activeReviewField?.id)
            ? `${pulseBorder} 1.5s infinite`
            : "none",
          cursor: "pointer",
        },
      };
  }, [disableCategorySelection, beginEditCategory, activeReviewField?.id]);

  const renderEmptyRowsFallback = useCallback(() => {
    return (
      <Box>
        <Typography
          sx={{ p: 4, display: "flex", justifyContent: "center" }}
          variant="body1"
          color="textSecondary"
        >
          No fee schedule categories
        </Typography>
      </Box>
    );
  }, []);

  const renderTopToolbarCustomActions = useCallback(() => {
    if (disableEditing) return null;
    return (
      <ActionsContainer>
        {!hideAddButton && (
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            size="small"
            onClick={onCreateCategoryClicked}
          >
            Add New Category
          </Button>
        )}
        {!hideExistingButton && (
          <Button
            startIcon={<SnippetFolderIcon />}
            variant="outlined"
            size="small"
            onClick={onExistingCategoryClicked}
          >
            Add Existing Category
          </Button>
        )}
        {!hideFromTemplateButton && (
          <Button
            startIcon={<FileCopyIcon />}
            size="small"
            onClick={onExistingTemplateClicked}
            variant="outlined"
          >
            From Existing Template
          </Button>
        )}
      </ActionsContainer>
    );
  }, [
    disableEditing,
    hideAddButton,
    onCreateCategoryClicked,
    hideExistingButton,
    onExistingCategoryClicked,
    hideFromTemplateButton,
    onExistingTemplateClicked
  ]);

  const renderToolbarInternalActions = useCallback((props: {
    table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
  }) => {
    const { table } = props;
    return (
      <Box>
        <ToggleGlobalFilterButton table={table} />
        <ToggleFiltersButton table={table} />
        <ShowHideColumnsButton table={table} />
        <ToggleDensePaddingButton table={table} />
        <ToggleFullScreenButton table={table} />
        {!hideCommentButton && (
          <CommentsButton
            commentThreads={commentThreads}
            disabled={disableComments}
            field={ProposalField.FeeSchedule}
            isActive={activeReviewField?.isEqualTo(ProposalField.FeeSchedule)}
            toolTip={commentsToolTip}
            onCommentsClicked={
              onCommentsClicked
                ? () => onCommentsClicked?.(ProposalField.FeeSchedule)
                : undefined
            }
          />
        )}
      </Box>
    )
  }, [
    commentThreads,
    disableComments,
    hideCommentButton,
    activeReviewField,
    commentsToolTip,
    onCommentsClicked
  ]);

  const renderRowActions = useCallback((props: {
    row: MRT_Row<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
  }) => {
    const { row } = props;
    return (
      <RowActionsContainer>
        <Tooltip title="Remove Category">
          <span>
            <IconButton
              onClick={(event) => handleRemoveButtonClicked(event, row.original)}
            >
              <DeleteIcon color="error" />
            </IconButton>
          </span>
        </Tooltip>
        {!hideCommentButton && (
          <CommentsButton
            field={ProposalField.FeeScheduleCategory(row.original.id)}
            isActive={activeReviewField?.isEqualTo(
              ProposalField.FeeScheduleCategory(row.original.id),
            )}
            commentThreads={commentThreads}
            disabled={disableComments}
            toolTip={commentsToolTip}
            onCommentsClicked={
              onCommentsClicked
                ? () => onCommentsClicked(
                  ProposalField.FeeScheduleCategory(row.original.id),
                  row.original.name.value,
                )
                : undefined
            }
          />
        )}
      </RowActionsContainer>
    );
  }, [
    handleRemoveButtonClicked,
    hideCommentButton,
    activeReviewField,
    commentThreads,
    disableComments,
    commentsToolTip,
    onCommentsClicked
  ]);

  const muiTableContainerProps = useCallback((props: {
    table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
  }) => {
    const { table } = props;

    const top = table.refs.topToolbarRef.current?.offsetHeight ?? 0;
    const bottom = table.refs.bottomToolbarRef.current?.offsetHeight ?? 0;
    const offset = issues?.length ? "48px" : "-12px";

    return {
      sx: {
        height: `calc(100% - ${top}px - ${bottom}px - ${offset})`,
        minHeight: 0,
      },
    }
  }, [issues]);

  // #region Table Options
  const tableOptions = useMemo<
    MRT_TableOptions<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
  >(
    () => ({
      columns,
      data: selectedCategories,
      muiPaginationProps: {
        color: "primary",
        shape: "rounded",
        showRowsPerPage: false,
        variant: "outlined",
      },
      paginationDisplayMode: "pages",
      enableRowSelection: false,
      enableTableHead: true,
      getRowId,
      initialState: {
        showColumnFilters: false,
        columnPinning: { right: ["mrt-row-actions"] },
        density: "comfortable",
      },
      enableColumnPinning: true,
      manualFiltering: true,
      enableStickyHeader: true,
      enableStickyFooter: true,
      manualPagination: true,
      manualSorting: true,
      enableRowActions: !hideActionsColumn && !disableEditing,
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setQuery,
      onSortingChange: setSorting,
      rowCount: selectedCategories.length,
      state: {
        columnFilters,
        globalFilter: query,
        sorting,
      },
      muiTableProps,
      muiTableBodyRowProps,
      renderEmptyRowsFallback,
      renderTopToolbarCustomActions,
      renderToolbarInternalActions,
      renderRowActions,
      muiTableContainerProps,
      muiTablePaperProps: {
        elevation: 0,
        sx: {
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
      },
    }),
    [
      columns,
      selectedCategories,
      getRowId,
      disableEditing,
      hideActionsColumn,
      columnFilters,
      query,
      sorting,
      muiTableContainerProps,
      muiTableProps,
      muiTableBodyRowProps,
      renderEmptyRowsFallback,
      renderTopToolbarCustomActions,
      renderToolbarInternalActions,
      renderRowActions
    ],
  );
  // #endregion

  // #region Render
  return (
    <TableContainer className={className}>
      {alertBannerMessage && (
        <Alert
          severity={alertBannerSeverity}
          sx={{
            mb: 2,
            width: "100%",
            whiteSpace: "normal",
            "& .MuiAlert-message": {
              width: "100%",
            },
          }}
        >
          {alertBannerMessage}
        </Alert>
      )}
      <MRTNoMemo<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
        options={tableOptions}
      />
    </TableContainer>
  );
  // #endregion
}

// #region Cells
const NameCell = ({ row, table }: {
  row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency },
  table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
}) => {
  if (table.getState().isLoading) return;
  if (isFeeScheduleTemplateLoadIssue(row.original)) {
    return (
      <Typography color="error">{`Error loading category: ${row.original.issue}`}</Typography>
    );
  }
  const value = row.original.name.value;
  return value ? <RowTitle>{value}</RowTitle> : <Icon>remove</Icon>;
};

const DescriptionCell = ({ row, table }: {
  row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency },
  table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
}) => {
  if (table.getState().isLoading || isFeeScheduleTemplateLoadIssue(row.original)) return;
  const value = row.original.description?.value;
  return value ?? <Icon>remove</Icon>;
};

const FixedFeeCell = ({ row, table }: {
  row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency },
  table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
}) => {
  if (table.getState().isLoading || isFeeScheduleTemplateLoadIssue(row.original)) return;
  let fixedValue;
  if (row.original instanceof FeeScheduleCategoryMaskedCurrency) {
    fixedValue = "Masked";
  } else if (row.original?.fee?.unit) {
    fixedValue = "No";
  } else {
    fixedValue = "Yes";
  }
  return fixedValue;
};

const UnitCell = ({ row, table }: {
  row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency },
  table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
}) => {
  if (table.getState().isLoading || isFeeScheduleTemplateLoadIssue(row.original)) return;
  return row.original instanceof FeeScheduleCategoryMaskedCurrency
    ? "Masked"
    : (row.original.fee?.unit ?? <Icon>remove</Icon>);
};

const BillingCodeCell = ({ row, table }: {
  row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency },
  table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
}) => {
  if (table.getState().isLoading || isFeeScheduleTemplateLoadIssue(row.original)) return;
  return row.original instanceof FeeScheduleCategoryMaskedCurrency
    ? "Masked"
    : (row.original.billingCode?.value ?? <Icon>remove</Icon>);
};
// #endregion
